import { createContext, useContext, useState, useCallback, useRef } from "react";
import { useLocation } from "wouter";

interface PageTransitionContextValue {
  navigateTo: (path: string) => void;
}

const PageTransitionContext = createContext<PageTransitionContextValue>({
  navigateTo: () => {},
});

export function PageTransitionProvider({ children }: { children: React.ReactNode }) {
  const [, setLocation] = useLocation();
  const [overlayOpacity, setOverlayOpacity] = useState(0);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const navigateTo = useCallback((path: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);

    setOverlayVisible(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setOverlayOpacity(1);
        timerRef.current = setTimeout(() => {
          setLocation(path);
          setOverlayOpacity(0);
          timerRef.current = setTimeout(() => {
            setOverlayVisible(false);
          }, 320);
        }, 300);
      });
    });
  }, [setLocation]);

  return (
    <PageTransitionContext.Provider value={{ navigateTo }}>
      {children}
      {overlayVisible && (
        <div
          aria-hidden="true"
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "var(--gray-50)",
            opacity: overlayOpacity,
            transition: "opacity 300ms ease",
            zIndex: 9999,
            pointerEvents: "none",
          }}
        />
      )}
    </PageTransitionContext.Provider>
  );
}

export function usePageTransition() {
  return useContext(PageTransitionContext);
}
